import { type ObservabilityMetadata, createPostHogProxyPath } from "@clientportal/core/observability";
import type { PostHogConfig } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import { DEPLOYMENT_ENVIRONMENT, registerPostHogObservabilityContext, SERVICE_VERSION } from "./correlation";
import { applyOriginalReferrerAttribution } from "./originalReferrer";

const PUBLIC_POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_UI_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST;

interface PostHogOptionsInput extends ObservabilityMetadata {
  origin?: string;
  projectKey?: string;
  uiHost?: string;
  search?: string;
  documentReferrer?: string;
}

export const createPostHogOptions = ({
  origin = window.location.origin,
  projectKey = PUBLIC_POSTHOG_KEY,
  uiHost = POSTHOG_UI_HOST,
  serviceVersion = SERVICE_VERSION,
  deploymentEnvironment = DEPLOYMENT_ENVIRONMENT,
  search = window.location.search,
  documentReferrer = document.referrer,
}: PostHogOptionsInput = {}): Partial<PostHogConfig> => ({
  api_host: `${origin}${createPostHogProxyPath(projectKey)}`,
  ui_host: uiHost,
  defaults: "2025-05-24",
  capture_exceptions: false,
  // The SDK fires the initial $pageview after `loaded` returns, so attribution
  // registered there is present on the first event.
  capture_pageview: "history_change",
  debug: import.meta.env.MODE === "development",
  loaded: (client) => {
    applyOriginalReferrerAttribution(client, search, documentReferrer);
    registerPostHogObservabilityContext(client, serviceVersion, deploymentEnvironment);
  },
});

export interface PosthogProviderProps {
  children: React.ReactNode;
}

export const isPostHogConfigured = (
  projectKey: string | undefined,
  uiHost: string | undefined,
  deploymentEnvironment: string | undefined,
): boolean => Boolean(projectKey && uiHost && deploymentEnvironment);

export default function PosthogProvider({ children }: PosthogProviderProps) {
  if (!isPostHogConfigured(PUBLIC_POSTHOG_KEY, POSTHOG_UI_HOST, DEPLOYMENT_ENVIRONMENT)) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={PUBLIC_POSTHOG_KEY} options={createPostHogOptions()}>
      {children}
    </PostHogProvider>
  );
}
